import { useState, useEffect, useRef } from "react";
import {
    Card,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@component/card";
import {
    CheckIcon,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { ProgressBar, ProgressStep } from "@component/progress-bar";
import { DailyProvider, useCallObject } from "@daily-co/daily-react";
import { DailyCall } from "@daily-co/daily-js";
import { VideoInterviewRef, CustomInterviewSection } from "@type/videoInterview";
import { catchErrorLog } from "@util/sentry";
import { SpeechDetectionProvider } from "@context/speech_detection_context";
import BasicLayout from "@container/layout/basic";
import CustomVoiceInterview from "@container/custom-voice-interview";
import Snackbar from "@component/snackbar";
import { InterviewAssessment } from "@type/assessments";
import { getAssessment } from "@service/assessment";
import { removeCookie } from "@util/cookie";

// Define interface for form data
interface UserFormData {
    email: string;
    startupName: string;
}

export default function FirstMoveInterview({
    routeTo = "/dashboard",
    showLayout = true,
    handleEnd
}: {
    routeTo: string,
    showLayout?: boolean,
    handleEnd?: () => void
}) {
    const navigate = useNavigate();
    const [interviewStage, setInterviewStage] = useState<string>("interview");
    const [isLoading, setIsLoading] = useState(true);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [userData, setUserData] = useState<UserFormData | null>(null);
    const [userId, setUserId] = useState<string>("");
    const [interviewStatus, setInterviewStatus] = useState<string>("");
    const videoInterviewRef = useRef<VideoInterviewRef>(null);
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);

    // New state for assessment data
    const [assessment, setAssessment] = useState<InterviewAssessment | null>(null);
    const [formattedSections, setFormattedSections] = useState<CustomInterviewSection[]>([]);
    const [isLoadingAssessment, setIsLoadingAssessment] = useState(true);
    const [isShowMessageRedirect, setIsShowMessageRedirect] = useState(false);
    const [, setAssessmentError] = useState<string | null>(null);

    // Hardcoded First Move interview ID
    const FIRST_MOVE_INTERVIEW_ID = import.meta.env.VITE_FIRST_MOVE_INTERVIEW_ID;

    // Environment variables
    const API_BASE_URL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
    const PIPECAT_API_URL = import.meta.env.VITE_PIPECAT_API_URL;
    const PIPECAT_AGENT_NAME = import.meta.env.VITE_PIPECAT_CUSTOM_AGENT_NAME;
    const PIPECAT_AUTH_TOKEN = import.meta.env.VITE_PIPECAT_AUTH_TOKEN;
    const AWS_ASSUME_ROLE_ARN = import.meta.env.VITE_AWS_ASSUME_ROLE_ARN;
    const RECORDING_S3_BUCKET_REGION = import.meta.env.VITE_AWS_S3_BUCKET_REGION;
    const RECORDING_S3_BUCKET_NAME = import.meta.env.VITE_AWS_FIRSTMOVE_S3_BUCKET_NAME;

    // First Move interviewer name constant
    const FIRST_MOVE_AI_NAME = "First Move AI Investor";

    // First Move logo path
    const FIRST_MOVE_LOGO_PATH = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/firstmovefund_logo.png`;

    // Fetch user data from localStorage and interview data from API on component mount
    useEffect(() => {
        setIsLoading(true);
        setIsLoadingAssessment(true);

        // Load user data from localStorage
        try {
            const storedData = localStorage.getItem('firstMoveFormData');
            if (storedData) {
                const parsedData = JSON.parse(storedData) as UserFormData;
                setUserData(parsedData);
                setUserId(localStorage.getItem("startupSubmissionId") || "anonymous-user");
                console.log("Loaded user id:", localStorage.getItem("startupSubmissionId"));
            }
        } catch (error) {
            catchErrorLog(`Error parsing stored form data: ${error}`);
        }

        setInterviewStatus("Pending Interview");
        setIsLoading(false);

        // Fetch assessment data using hardcoded Firstmove ID
        const fetchAssessmentData = async () => {
            try {
                setAssessmentError(null);
                console.log("Fetching First Move interview assessment data...");
                console.log("Using First Move Interview ID:", FIRST_MOVE_INTERVIEW_ID);
                const response = await getAssessment(FIRST_MOVE_INTERVIEW_ID);
                if (response) {
                    console.log("First Move Assessment response:", response);
                    const assessmentData = response as InterviewAssessment;
                    setAssessment(assessmentData);

                    // Format sections from the assessment
                    if (assessmentData?.content?.sections) {
                        const sections = assessmentData.content.sections.map((section: any) => ({
                            title: section.title,
                            instruction: section.instruction,
                            duration: section.duration,
                            questions: section.questions?.map((q: any) => q.text).filter((text: string) => text?.trim() !== "") || undefined,
                            transition: section.transition || undefined
                        }));
                        console.log("Formatted First Move Sections:", sections);
                        setFormattedSections(sections as CustomInterviewSection[]);
                    } else {
                        // Fallback to hardcoded sections if needed
                        console.log("Using default First Move sections (assessment didn't contain sections)");
                    }
                } else {
                    console.error("Failed to fetch First Move assessment, using default sections");
                    setAssessmentError("Failed to load screening data");
                }
            } catch (err) {
                catchErrorLog(`Error fetching First Move assessment data: ${err}`);
                setAssessmentError("An unexpected error occurred loading interview data");
            } finally {
                setIsLoadingAssessment(false);
            }
        };

        fetchAssessmentData();
    }, []);

    useEffect(() => {
        if (interviewStage === "complete" || interviewStatus === 'Complete') {
            setTimeout(() => {
                setIsShowMessageRedirect(true);
            }, 1000); // Redirect after 1 second

            setTimeout(() => {
                console.log("Redirecting to First Move website after interview completion");
                window.location.href = "https://www.firstmovefund.com";
            }, 5000); // Redirect after 5 seconds
        }

    }, [interviewStage, interviewStatus]);


    const steps: ProgressStep[] = [
        {
            label: "AI Interview",
            isCompleted: currentStep > 1,
            isActive: currentStep === 1,
            className: "!bg-black",
        },
        {
            label: "Complete",
            isCompleted: currentStep > 2,
            isActive: currentStep === 2,
            className: "!bg-black",
        },
    ];

    // Get candidate name for conversation name
    const getCandidateName = () => {
        if (!userData) return "Anonymous Founder";
        return userData.email || "Anonymous Founder";
    };

    // Format user data for the AI context
    const formatUserData = (data: UserFormData | null) => {
        if (!data) return "";

        return `
Email: ${data.email}
Startup Name: ${data.startupName}
        `.trim();
    };

    // First Move interview configuration
    const getInterviewConfig = () => {
        // Use assessment data if available, otherwise use defaults
        return {
            conversationName: assessment?.content.interviewName ||
                `${getCandidateName()}: First Move AI Vibe Check (${new Date().toISOString()})`,
            conversationalContext: assessment?.content.interviewContext ||
                `You are a ${FIRST_MOVE_AI_NAME}. This is not a pitch, but think of it as a vibe check - to get to know the founder better.

Be conversational, friendly, and genuinely curious. Ask follow-up questions based on their responses. Show interest in their journey and story.

Remember: This is about building rapport and understanding the founder, not evaluating a formal pitch deck.`,
            interviewName: assessment?.content.interviewName || "First Move AI Vibe Check",
            customGreeting: assessment?.content.customGreeting ||
                `Hi there! I'm your ${FIRST_MOVE_AI_NAME}. Great to meet you! I'm excited to learn about you and your startup journey. Shall we dive in?`,
            description: assessment?.content.description || (
                <>
                    A conversational interview with {FIRST_MOVE_AI_NAME} to assess founder-market fit, <br />
                    traction, and funding needs in a friendly, vibe-check format.
                </>
            ),
            closingStatement: assessment?.content.closingStatement || "",
            interviewerPersona: assessment?.content.interviewerPersona || "",
            toneAndCommunication: assessment?.content.toneAndCommunication || undefined,
            interviewFlowPrinciples: assessment?.content.interviewFlowPrinciples || undefined,
            informationToShare: assessment?.content.informationToShare || undefined,
            maxDuration: assessment?.content.maxDuration ||
                formattedSections.reduce((total, section) => total + section.duration, 0), // Total duration in seconds
        };
    };

    // Handle completion of interview and cleanup
    const handleInterviewComplete = () => {
        setIsSnackbarOpen(true);
        if (videoInterviewRef.current) {
            console.log("Ending interview and cleaning up media resources");
            videoInterviewRef.current.endInterview('interview_completed');
        }

        setInterviewStage("complete");
        setCurrentStep(2);
        if (handleEnd) {
            setInterviewStatus("Complete");
            handleEnd();
        }
    };

    // Clean up any media when showing the completed screen directly due to status
    useEffect(() => {
        if (interviewStatus === 'Complete' && interviewStage === "interview") {
            console.log("Interview already completed - cleaning up resources");
            setInterviewStage("complete");
            setCurrentStep(2);

            if (handleEnd) {
                setInterviewStatus("Complete");
                handleEnd();
            }
        }
    }, [interviewStatus, interviewStage]);

    const handleSnackbarClose = () => {
        setIsSnackbarOpen(false);
        // Remove the auth token from cookies
        removeCookie("authToken");
    };

    const callObject = useCallObject({}) as DailyCall;

    if (isLoading || isLoadingAssessment) {
        return <div>Loading...</div>;
    }

    // Get the interview configuration based on assessment data
    const INTERVIEW_CONFIG = getInterviewConfig();

    if (showLayout) {
        return (
            <>
                <BasicLayout
                    customLogo={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/firstmovefund_logo.jpeg`}
                    disableMinHeight
                    hideNavbarMenu
                    style={{
                        height: "calc(100vh - 57px)", // Adjust height to fit below the header
                    }}
                    showFooter={false}
                >
                    <div className="container max-w-6xl mx-auto px-4 sm:py-8 flex flex-col min-h-[510px] items-center justify-center mt-6 sm:mt-0">
                        {interviewStage === "interview" && interviewStatus !== 'Complete' && !isLoading ? (
                            <DailyProvider callObject={callObject}>
                                <SpeechDetectionProvider
                                    volumeThreshold={30}
                                    silenceDelay={400}
                                    autoStart={true}
                                >
                                    <CustomVoiceInterview
                                        ref={videoInterviewRef}
                                        requireSignIn={false}
                                        interviewId={FIRST_MOVE_INTERVIEW_ID}
                                        pipecatApiUrl={PIPECAT_API_URL}
                                        agentName={PIPECAT_AGENT_NAME}
                                        authToken={PIPECAT_AUTH_TOKEN}
                                        interviewContext={INTERVIEW_CONFIG.conversationalContext}
                                        closingStatement={INTERVIEW_CONFIG.closingStatement}
                                        interviewerPersona={INTERVIEW_CONFIG.interviewerPersona || ""}
                                        toneAndCommunication={INTERVIEW_CONFIG.toneAndCommunication || undefined}
                                        interviewFlowPrinciples={INTERVIEW_CONFIG.interviewFlowPrinciples || undefined}
                                        informationToShare={INTERVIEW_CONFIG.informationToShare || undefined}
                                        s3BucketName={RECORDING_S3_BUCKET_NAME}
                                        s3BucketRegion={RECORDING_S3_BUCKET_REGION}
                                        awsAssumeRoleArn={AWS_ASSUME_ROLE_ARN}
                                        interviewName={INTERVIEW_CONFIG.interviewName}
                                        fastapiURL={import.meta.env.VITE_FIRST_MOVE_API || API_BASE_URL}
                                        onCloseModal={handleInterviewComplete}
                                        customGreeting={INTERVIEW_CONFIG.customGreeting}
                                        userId={userId}
                                        userCV={formatUserData(userData)}
                                        onCompletedInterview={() => {
                                            console.log("First Move interview completed");
                                            // handleInterviewComplete();
                                        }}
                                        userName={userData?.email || ""}
                                        sections={formattedSections}
                                        description={INTERVIEW_CONFIG.description}
                                        maxDuration={INTERVIEW_CONFIG.maxDuration}
                                        aiName={assessment?.content.aiName || FIRST_MOVE_AI_NAME}
                                        aiLogoSrc={FIRST_MOVE_LOGO_PATH}
                                        language="FirstMove"
                                        userEmail={userData?.email || ""}
                                        concludingStatements="Thanks for your time and for sharing about <their_startup_name> today. We’ll pass the notes to Audra and Joel at First Move, and if it aligns with their mandate, we’ll be in touch soon with next steps."
                                        voice={"female"}
                                    />
                                </SpeechDetectionProvider>
                            </DailyProvider>
                        ) : null}
                        {(interviewStage === "complete" || interviewStatus === 'Complete') && (
                            <>
                                <Card>
                                    <CardHeader>
                                        <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                                        </div>
                                        <CardTitle className="text-center text-2xl">
                                            First Move Interview Complete!
                                        </CardTitle>
                                        <CardDescription className="text-center">
                                            <p className="text-black">
                                                Thank you for completing your interview with <strong>{FIRST_MOVE_AI_NAME}.</strong>
                                                <br />
                                                We'll review your responses and get back to you about next steps in the investment process.
                                                {isShowMessageRedirect && (
                                                    <>
                                                        <br /><br /><br />
                                                        <span className="text-gray-700">
                                                            Redirecting you back to First Move
                                                            <span className="inline-block">
                                                                <span className="animate-pulse">.</span>
                                                                <span className="animate-pulse" style={{ animationDelay: '0.2s' }}>.</span>
                                                                <span className="animate-pulse" style={{ animationDelay: '0.4s' }}>.</span>
                                                            </span>
                                                            <br />
                                                            If not, <Link className="text-blue-500 hover:underline" to="https://www.firstmovefund.com">click here.</Link>
                                                        </span>
                                                    </>
                                                )}
                                            </p>
                                        </CardDescription>
                                    </CardHeader>
                                </Card>
                            </>
                        )}
                    </div>
                </BasicLayout>

                {/* Progress Bar */}
                {interviewStage === "interview" && interviewStatus !== 'Complete' && (
                    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t p-4">
                        <div className="container max-w-5xl mx-auto">
                            <ProgressBar currentStep={currentStep} steps={steps} />
                        </div>
                    </div>
                )}

                {/* Success Snackbar */}
                <Snackbar
                    message="Interview completed successfully!"
                    variant="success"
                    isOpen={isSnackbarOpen}
                    onClose={handleSnackbarClose}
                    duration={3000}
                />
            </>
        );
    }

    // Version without layout (for embedding in other components)
    return (
        <div className="w-full">
            {interviewStage === "interview" && interviewStatus !== 'Complete' && !isLoading ? (
                <DailyProvider callObject={callObject}>
                    <SpeechDetectionProvider
                        volumeThreshold={30}
                        silenceDelay={400}
                        autoStart={true}
                    >
                        <CustomVoiceInterview
                            ref={videoInterviewRef}
                            requireSignIn={false}
                            interviewId={FIRST_MOVE_INTERVIEW_ID}
                            pipecatApiUrl={PIPECAT_API_URL}
                            agentName={PIPECAT_AGENT_NAME}
                            authToken={PIPECAT_AUTH_TOKEN}
                            interviewContext={INTERVIEW_CONFIG.conversationalContext}
                            closingStatement={INTERVIEW_CONFIG.closingStatement}
                            interviewerPersona={INTERVIEW_CONFIG.interviewerPersona || ""}
                            toneAndCommunication={INTERVIEW_CONFIG.toneAndCommunication || undefined}
                            interviewFlowPrinciples={INTERVIEW_CONFIG.interviewFlowPrinciples || undefined}
                            informationToShare={INTERVIEW_CONFIG.informationToShare || undefined}
                            s3BucketName={RECORDING_S3_BUCKET_NAME}
                            s3BucketRegion={RECORDING_S3_BUCKET_REGION}
                            awsAssumeRoleArn={AWS_ASSUME_ROLE_ARN}
                            interviewName={INTERVIEW_CONFIG.interviewName}
                            fastapiURL={import.meta.env.VITE_FIRST_MOVE_API || API_BASE_URL}
                            onCloseModal={handleInterviewComplete}
                            customGreeting={INTERVIEW_CONFIG.customGreeting}
                            userId={userId}
                            userCV={formatUserData(userData)}
                            onCompletedInterview={() => {
                                console.log("First Move interview completed");
                                handleInterviewComplete();
                            }}
                            userName={userData?.email || ""}
                            sections={formattedSections}
                            description={INTERVIEW_CONFIG.description}
                            maxDuration={INTERVIEW_CONFIG.maxDuration}
                            aiName={assessment?.content.aiName || FIRST_MOVE_AI_NAME}
                            language="FirstMove"
                            aiLogoSrc={FIRST_MOVE_LOGO_PATH}
                            userEmail={userData?.email || ""}
                            concludingStatements="Thanks for your time and for sharing about <their_startup_name> today. We’ll pass the notes to Audra and Joel at First Move, and if it aligns with their mandate, we’ll be in touch soon with next steps."
                            voice={"female"}
                        />
                    </SpeechDetectionProvider>
                </DailyProvider>) : (
                <Card>
                    <CardHeader>
                        <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                        </div>
                        <CardTitle className="text-center text-2xl">
                            First Move Interview Complete!
                        </CardTitle>
                        <CardDescription className="text-center">
                            <p className="text-black">
                                Thank you for completing your interview with <strong>{FIRST_MOVE_AI_NAME}.</strong>
                                <br />
                                We'll review your responses and get back to you about next steps in the investment process.
                                <br />
                                {isShowMessageRedirect && (
                                    <>
                                        <br /><br /><br />
                                        <span className="text-gray-700">
                                            Redirecting you back to First Move
                                            <span className="inline-block">
                                                <span className="animate-pulse">.</span>
                                                <span className="animate-pulse" style={{ animationDelay: '0.2s' }}>.</span>
                                                <span className="animate-pulse" style={{ animationDelay: '0.4s' }}>.</span>
                                            </span>
                                            <br />
                                            If not, <Link className="text-blue-500 hover:underline" to="https://www.firstmovefund.com">click here.</Link>
                                        </span>
                                    </>
                                )}
                            </p>
                        </CardDescription>
                    </CardHeader>
                </Card>
            )}

            {/* Success Snackbar */}
            <Snackbar
                message="Interview completed successfully!"
                variant="success"
                isOpen={isSnackbarOpen}
                onClose={handleSnackbarClose}
                duration={3000}
            />
        </div>
    );
}
